import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowLeft } from 'lucide-react';
import { PageTransition } from '../transitions/PageTransition';
import { ContactForm } from './ContactForm';
import { ContactInfo } from './ContactInfo';
import { useCursorContext } from '../../hooks/useCursorContext';
import { SEOHead } from '../seo/SEOHead';

gsap.registerPlugin(ScrollTrigger);

export function ContactPage() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { setCursorState } = useCursorContext();

  useEffect(() => {
    let ctx: gsap.Context;

    const timer = setTimeout(() => {
      ctx = gsap.context(() => {
        // Animate immediately - no ScrollTrigger needed for above-fold content
        gsap.fromTo(
          '.contact-animate',
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.1,
            ease: 'power2.out',
          }
        );
      }, sectionRef);
    }, 150);

    return () => {
      clearTimeout(timer);
      if (ctx) ctx.revert();
    };
  }, []);

  return (
    <PageTransition>
      <SEOHead
        title="Kontakt"
        description="Kontaktieren Sie Niko Knez für Ihre Webentwicklung und UX/UI Design Projekte. Antwort innerhalb von 24 Stunden."
        path="/contact"
      />
      <div ref={sectionRef} className="min-h-screen bg-[#0a0a0a]">
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 px-8 md:px-12 lg:px-24">
          {/* Background gradient */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500/10 rounded-full blur-[200px]" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto">
            {/* Back link */}
            <Link
              to="/"
              className="contact-animate inline-flex items-center gap-3 text-white/40 hover:text-emerald-500 transition-colors group mb-12"
              onMouseEnter={() => setCursorState('hovering')}
              onMouseLeave={() => setCursorState('default')}
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-2 transition-transform" />
              <span className="text-sm tracking-wider uppercase">Zurück</span>
            </Link>

            {/* Title */}
            <h1 className="contact-animate text-5xl md:text-7xl lg:text-8xl font-light text-white leading-tight mb-6">
              Lass uns
              <br />
              <span className="text-emerald-500">zusammenarbeiten</span>
            </h1>

            <p className="contact-animate text-xl text-white/50 max-w-2xl">
              Haben Sie ein Projekt im Sinn? Ich freue mich auf Ihre Nachricht und
              antworte in der Regel innerhalb von 24 Stunden.
            </p>
          </div>
        </section>

        {/* Contact Grid */}
        <section className="py-16 px-8 md:px-12 lg:px-24">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
              {/* Left: Form */}
              <div className="contact-animate">
                <h2 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-8">
                  Nachricht senden
                </h2>
                <ContactForm />
              </div>

              {/* Right: Info */}
              <div className="contact-animate">
                <h2 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-8">
                  Kontaktinformationen
                </h2>
                <ContactInfo />
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-8 md:px-12 lg:px-24 border-t border-white/10">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <span className="text-xs text-white/20">
              © {new Date().getFullYear()} NK Design
            </span>
            <span className="text-xs text-white/20">
              Köln, Deutschland
            </span>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
}
